import { Map as GLMap, type MapLibreEvent, type MapOptions } from "maplibre-gl"
import { Event } from "maplibre-gl/src/util/evented"
import Point from "@mapbox/point-geometry"
import { getViewRect } from "./getViewableRect"

function mercatorXfromLng(lng: number): number {
    return (180 + lng) / 360
}

function mercatorYfromLat(lat: number): number {
    return (
        (180 -
            (180 / Math.PI) *
                Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))) /
        360
    )
}

/**
 * constrain n to the given range, excluding the minimum, via modular arithmetic
 *
 * @param n value
 * @param min the minimum value to be returned, exclusive
 * @param max the maximum value to be returned, inclusive
 * @returns constrained number
 * @private
 */
function wrap(n: number, min: number, max: number): number {
    const d = max - min
    const w = ((((n - min) % d) + d) % d) + min
    return w === min ? max : w
}

/**
 * Interprets if the user is trying to "stubbornly" return to the globe by zooming out
 */
class StubbornZoomHandler extends EventTarget {
    #onZoomCount: any
    #onZoomStartCount: number
    constructor() {
        super()
        this.#onZoomCount = 0
        this.#onZoomStartCount = 0
    }

    onZoom(): void {
        this.#onZoomCount++
        if (this.shouldStubbornZoom()) {
            this.doStubbornZoom()
        }
    }

    onZoomStart(): void {
        this.#onZoomStartCount++
        if (this.shouldStubbornZoom()) {
            this.doStubbornZoom()
        }
    }

    shouldStubbornZoom(): boolean {
        return (
            (this.#onZoomCount > 50 && this.#onZoomStartCount >= 2) ||
            this.#onZoomCount > 1000
        )
    }

    doStubbornZoom(): void {
        this.dispatchEvent(new CustomEvent("stubbornzoom", { detail: null }))
        this.reset()
    }

    reset(): void {
        this.#onZoomCount = 0
        this.#onZoomStartCount = 0
    }
}

export class Map extends GLMap {
    #listeners
    stubbornZoomHandler: StubbornZoomHandler
    constructor(options: MapOptions) {
        super(options)
        this.#listeners = []
        this.stubbornZoomHandler = new StubbornZoomHandler()
        this.stubbornZoomHandler.addEventListener("stubbornzoom", (event) => {
            this.fire(new Event("stubbornzoom"))
        })

        this.on(
            "zoom",
            (
                event: MapLibreEvent<
                    MouseEvent | TouchEvent | WheelEvent | undefined
                > & { cause?: string }
            ) => {
                if (event.cause !== undefined) return
                if (!this.atMaxBounds()) return
                this.stubbornZoomHandler.onZoom()
            }
        )

        this.on(
            "zoomstart",
            (
                event: MapLibreEvent<
                    MouseEvent | TouchEvent | WheelEvent | undefined
                > & { cause?: string }
            ) => {
                if (event.cause !== undefined) return
                if (!this.atMaxBounds()) return
                this.stubbornZoomHandler.onZoomStart()
            }
        )
    }

    trackedOn(type, layer, listener): void {
        super.on(type, layer, listener)
        this.#listeners.push([type, layer, listener])
    }

    trackedOff(type, layer, listener): void {
        super.off(type, layer, listener)
        // Remove just the entry where all the parameters match
        this.#listeners = this.#listeners.filter(
            ([t, lay, lis]) =>
                !(t === type && lay === layer && lis === listener)
        )
    }

    atMaxBounds(): boolean {
        const viewport = getViewRect()
        const viewportHeight = viewport.bottom - viewport.top
        const viewportWidth = viewport.right - viewport.left

        const latRange = this.transform.latRange as [number, number] | null
        const lngRange = this.transform.lngRange as [number, number] | null
        if (latRange === null || lngRange === null) return false

        const minY = mercatorYfromLat(latRange[1]) * this.transform.worldSize
        const maxY = mercatorYfromLat(latRange[0]) * this.transform.worldSize
        const minX = wrap(
            mercatorXfromLng(lngRange[0]) * this.transform.worldSize,
            0,
            this.transform.worldSize
        )
        let maxX = wrap(
            mercatorXfromLng(lngRange[1]) * this.transform.worldSize,
            0,
            this.transform.worldSize
        )
        if (maxX < minX) maxX += this.transform.worldSize

        const atLngBound = viewportWidth >= maxX - minX - 0.1
        const atLatBound = viewportHeight >= maxY - minY - 0.1
        return atLngBound && atLatBound
    }

    /**
     * Remove all user-added event listeners
     */
    offAll(): void {
        this.#listeners.forEach((entry: Parameters<Map["off"]>) => {
            super.off(...entry)
            console.log("removed event listener", entry)
        })
        this.#listeners = []
    }
}
